import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// Page transition wrapper
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Animated section component
function AnimatedSection({ children, className, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

function About() {
  const skills = [
    {
      category: 'Experience Design',
      icon: '◇',
      color: 'purple',
      items: ['Spatial Design', 'Environmental Storytelling', 'Wayfinding', 'Guest Journey Mapping', 'Interactive Elements'],
    },
    {
      category: 'Production & Leadership',
      icon: '✦',
      color: 'pink',
      items: ['Team Management', 'Production Scheduling', 'Budget Management', 'Vendor Coordination', 'Event Management'],
    },
    {
      category: 'Technical & Fabrication',
      icon: '⬡',
      color: 'teal',
      items: ['CNC Routing', 'Laser Cutting', 'Scale Modeling', 'Lighting Design', 'Foam Carving'],
    },
    {
      category: 'Digital & Creative',
      icon: '◆',
      color: 'warm',
      items: ['Product Design', 'Creative Coding (p5.js)', 'Motion Graphics', 'After Effects', 'Figma'],
    },
  ];

  return (
    <motion.div
      className="about-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      {/* Page Hero - Playful */}
      <section className="page-hero about-hero playful">
        <div className="container">
          <div className="about-hero-content">
            <motion.div
              className="hero-image-wrapper"
              initial={{ opacity: 0, scale: 0.95, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: -3 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="hero-image">
                <img src="/images/about/me-headshot.jpg" alt="Samantha de Nicola" />
              </div>
            </motion.div>

            <motion.div
              className="hero-text"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="page-label">About Me</span>
              <h1 className="page-title playful-title">
                Hi, I'm<br/>
                <span className="title-italic">Samantha</span>
              </h1>
              <p className="hero-tagline">
                I create worlds people <strong><em>want</em></strong> to step into.
              </p>
              <p className="hero-description">
                I'm an experiential designer who believes the best experiences engage
                multiple senses, reward curiosity, and make people <em>feel</em> something
                they'll remember.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section - Playful */}
      <section className="philosophy-section playful">
        <div className="container">
          <AnimatedSection className="philosophy-content">
            <div className="philosophy-text">
              <h2>My <em>Approach</em></h2>
              <p>
                Whether it's a large-scale themed installation or a micro-moment of
                discovery in a mobile app, I design experiences that tell stories and
                invite exploration. I believe the magic happens at the intersection of
                physical and digital, of spectacle and intimacy, of planned moments and
                unexpected discoveries.
              </p>
              <p>
                My background spans themed entertainment production, digital product design,
                and interactive installations—with coursework in Music Festival Design &
                Management, Live Experience Production, and Digital & Live Experience Design.
                I bring hands-on fabrication skills, production management, and user-centered
                design thinking to every project.
              </p>
            </div>
            <div className="philosophy-visual">
              <div className="visual-grid">
                <motion.div
                  className="visual-item comparison-image"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="comparison-wrapper">
                    <img src="/images/about/building-1.jpg" alt="Concept sketch" />
                    <img src="/images/about/building-2.jpg" alt="Built environment" />
                  </div>
                </motion.div>
                <motion.div
                  className="visual-item"
                  whileHover={{ scale: 1.05 }}
                >
                  <img src="/images/about/testing-mini-golf.JPG" alt="Testing mini golf installation" />
                </motion.div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* What Drives Me Section */}
      <section className="drives-section playful">
        <div className="container">
          <AnimatedSection className="drives-content">
            <h2>What <em>Drives</em> Me</h2>
            <p className="drives-intro">
              I'm obsessed with experiences that bring content to life for fans: concert
              activations, artist popups, premiere events, immersive exhibits.
            </p>
            <p>
              Think Meow Wolf's layered worlds where every corner rewards curiosity.
              teamLab's sensory immersion that makes you forget time exists. The Gracie
              Abrams popup where fans found her favorite books alongside merch. The
              Olivia Rodrigo premiere after-party where fans felt like they stepped
              into the movie. Percy Jackson's premiere where cast and fans walked
              through recreated Camp Half-Blood cabins together.
            </p>
            <p>
              I want superfans to feel like they've stepped into the pages of their
              favorite book, the mind of their favorite artist, or onto the set of
              their favorite show. Experiences that use nuanced detail and multimedia, both
              digital and physical, to bring worlds to life in ways that make people
              feel <em>part</em> of something magical.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Skills Section - Playful */}
      <section className="skills-section playful">
        <div className="container">
          <AnimatedSection className="section-header playful">
            <h2>Skills & <em>Expertise</em></h2>
            <p>A blend of creative vision and technical execution</p>
          </AnimatedSection>

          <div className="skills-grid">
            {skills.map((skillGroup, index) => (
              <motion.div
                key={skillGroup.category}
                className={`skill-card skill-${skillGroup.color}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="skill-card-header">
                  <span className="skill-icon">{skillGroup.icon}</span>
                  <h3>{skillGroup.category}</h3>
                </div>
                <div className="skill-tags">
                  {skillGroup.items.map((skill) => (
                    <span key={skill} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What I'm Looking For - Playful */}
      <section className="seeking-section playful">
        <div className="container">
          <AnimatedSection className="seeking-content">
            <h2>What I'm <em>Looking For</em></h2>
            <p className="seeking-intro">
              I'm currently seeking opportunities in experiential design at
              companies creating immersive installations, themed attractions,
              live events, and fan experiences.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Beyond Work Section - Playful */}
      <section className="beyond-section playful">
        <div className="container">
          <AnimatedSection className="beyond-content">
            <h2>Beyond <em>Work</em></h2>
            <p className="beyond-intro">
              When I'm not designing experiences, you'll find me...
            </p>
            <div className="beyond-grid">
              <motion.div
                className="beyond-item"
                whileHover={{ y: -5 }}
              >
                <div className="beyond-icon">&#9671;</div>
                <h3>Working with Clay</h3>
                <p>
                  Ceramics as a practice in patience and presence - creating
                  objects that invite touch and connection.
                </p>
              </motion.div>
              <motion.div
                className="beyond-item"
                whileHover={{ y: -5 }}
              >
                <div className="beyond-icon">&#9670;</div>
                <h3>Capturing Moments</h3>
                <p>
                  Photography as a way of seeing - finding stories in light,
                  shadow, and unexpected details.
                </p>
              </motion.div>
              <motion.div
                className="beyond-item"
                whileHover={{ y: -5 }}
              >
                <div className="beyond-icon">&#9674;</div>
                <h3>Seeking Immersion</h3>
                <p>
                  Always hunting for the next immersive art installation,
                  escape room, or theatrical experience to get lost in.
                </p>
              </motion.div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact CTA - Simple */}
      <section className="contact-section-simple">
        <div className="container">
          <AnimatedSection className="contact-simple">
            <h2>Let's <em>Connect</em></h2>
            <a href="mailto:sami@samidenicola.com" className="contact-email-link">
              sami@samidenicola.com
            </a>
          </AnimatedSection>
        </div>
      </section>
    </motion.div>
  );
}

export default About;
